<template>
  <v-card width="1000">
    <v-card-title>
      <v-container>
        <v-row>
          <v-col cols="auto" class="font-title">HawkSoft Seat Management</v-col>
          <v-spacer></v-spacer>
          <v-col cols="auto">
            <v-btn icon @click="returnToMarketplace">
              <v-icon>clear</v-icon>
            </v-btn>
          </v-col>
        </v-row>
        <v-row class="body-2 font-italic">
          <v-col cols="auto" class="py-0">
            Customer ID: {{ agency.agencyId }}
          </v-col>
          <v-spacer></v-spacer>
        </v-row>
      </v-container>
    </v-card-title>
    <v-card-text>
      <v-progress-circular v-if="loading" color="primary" indeterminate size="32" />
      <v-container v-if="!loading" fluid>
        <v-row class="px-3">
          <div class="text-start">
            <span v-if="displayPendingVerbage">
              <span class="font-weight-bold">Your agency has a <span class="underline">pending</span> seat request change. </span>
              <span>
                The seat change request will
                <span class="font-italic">{{ pendingSeats > 0 ? 'increase' : 'decrease' }}</span>
                the number of seats on your account from {{ seatCount }} to a
                <span class="font-weight-bold">total of {{ currentPlusPendingSeats }} seats.</span>
                <span>You may update your seat change request by using the + or - buttons. Once a seat change is being processed, it can no longer be changed.</span>
              </span>
            </span>
            <span v-if="displayUpdatedVerbage">
              <span class="font-weight-bold">Your seat request has been <span class="underline">updated</span>.</span>
              <span>
                The seat change request will
                <span class="font-italic">{{ pendingSeats > 0 ? 'increase' : 'decrease' }}</span>
                the number of seats on your account from {{ seatCount }} to a
                <span class="font-weight-bold">total of {{ currentPlusPendingSeats }} seats.</span>
                Your request is currently pending processing, click the button below to return to the Marketplace.
              </span>
            </span>
            <span v-if="displayCurrentlyInProcessVerbage">
              <span class="font-weight-bold">Your agency has a seat request change currently <span class="underline">being processed</span>.</span>
              <span>
                The seat change request will
                <span class="font-italic">{{ pendingSeats > 0 ? 'increase' : 'decrease' }}</span>
                the number of seats on your account from {{ seatCount }} to a
                <span class="font-weight-bold">total of {{ currentPlusPendingSeats }} seats.</span>
                A new request cannot be submitted until this transaction has been completed.
              </span>
            </span>
            <span v-if="displayNewRequestVerbage">
              <span class="font-weight-bold">Your agency is currently licensed for {{ seatCount }} seats.</span>
              <span>
                {{ newSeatChangeVerbage }}
              </span>
            </span>
          </div>
        </v-row>
        <v-row class="px-3 pt-3" align="center" justify="center">
          <v-spacer></v-spacer>
          <v-col class="px-0" cols="1">
            <v-btn fab small outlined color="#004C97" :disabled="disableDecrement" @click="decrement">
              <v-icon dark>remove</v-icon>
            </v-btn>
          </v-col>
          <v-col class="px-0" cols="auto">
            <div class="display-1 hs-blue">{{ displayedSeatCount }}</div>
            <div class="caption text-uppercase"><span v-if="calcSeatsDifference !== 0">Desired</span> Total Seats</div>
          </v-col>
          <v-col class="px-0" cols="1">
            <v-btn fab small outlined color="#004C97" :disabled="disableIncrement" @click="increment">
              <v-icon dark>add</v-icon>
            </v-btn>
          </v-col>
          <v-spacer></v-spacer>
        </v-row>
        <v-alert v-if="displayPriceWarning" type="warning" dense outlined prominent><b>This change will impact the cost of future invoices you receive from HawkSoft.</b> By submitting this request, you agree to these changes.</v-alert>
        <v-alert v-if="displayMinWarning" type="info" dense outlined prominent><b>HawkSoft requires a minimum of 3 seats.</b></v-alert>
        <v-alert v-model="displayErrorAlert" type="error" dense outlined prominent dismissible>Error sending seat change request, please try again or contact customer support.</v-alert>
        <v-alert v-if="isInProcess" type="warning" dense outlined prominent>Change request is currently being processed, unable to change request until completed</v-alert>
      </v-container>
    </v-card-text>
    <v-card-actions>
      <v-container>
        <v-row>
          <v-col cols="auto">
            <v-btn depressed tile color="#004C97" class="text-uppercase white--text" :disabled="updatingSeatRequest" @click="submit">{{ actionButtonText }}</v-btn>
            <v-btn depressed tile @click="returnToMarketplace">Cancel</v-btn>
          </v-col>
          <v-spacer />
        </v-row>
      </v-container>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapState } from 'vuex';

export default {
  name: 'LocalAgencySeats',
  props: {
    seatCount: {
      type: Number,
      required: true
    }
  },
  data () {
    return {
      requestSent: false,
      displayErrorAlert: false,
      pendingRequest: {},
      currentSessionQuantityChange: 0,
      newSeatChangeRequestCreated: false,
      loading: true,
      updatingSeatRequest: false,
      newSeatChangeVerbage: 'A seat represents one concurrent user accessing HawkSoft CMS at a time. Use the + and - buttons to adjust the total number of seats then click \'submit\' to complete the request. Updates will be made to your account within 24 hours.'
    };
  },
  computed: {
    ...mapState(['agency', 'agencyUser']),
    displayedSeatCount () {
      return this.pendingSeats + this.seatCount + this.currentSessionQuantityChange;
    },
    pendingSeats () {
      return this.noPendingRequest ? 0 : this.pendingRequest.quantity;
    },
    calcSeatsDifference () {
      return this.displayedSeatCount - this.seatCount;
    },
    currentPlusPendingSeats () {
      return this.seatCount + this.pendingSeats;
    },
    disableDecrement () {
      return this.displayedSeatCount < 4 || this.isInProcess || this.updatingSeatRequest;
    },
    disableIncrement () {
      return this.isInProcess || this.updatingSeatRequest;
    },
    displayPriceWarning () {
      return this.currentSessionQuantityChange !== 0;
    },
    displayMinWarning () {
      return this.displayedSeatCount < 4;
    },
    isInProcess () {
      return this.pendingRequest.status === 'InProcess';
    },
    noPendingRequest () {
      return Object.keys(this.pendingRequest).length === 0;
    },
    displayPendingVerbage () {
      // if there are pending seats and the request has not been sent and the pending request is not in process OR we just added a new request
      return (!this.noPendingRequest && !this.requestSent && !this.displayCurrentlyInProcessVerbage) || this.newSeatChangeRequestCreated;
    },
    displayUpdatedVerbage () {
      // if there are pending seats and the request has been sent and it is an existing request
      return this.pendingSeats !== 0 && this.requestSent && !this.newSeatChangeRequestCreated;
    },
    displayCurrentlyInProcessVerbage () {
      // if there is a pending request exists and it is in process
      return !this.noPendingRequest && this.isInProcess;
    },
    displayNewRequestVerbage () {
      // if there is not a pending request and a new request has not been sent yet
      return this.noPendingRequest;
    },
    actionButtonText () {
      if (this.pendingSeats === 0) {
        return 'submit';
      }
      if (this.displayedSeatCount === this.currentPlusPendingSeats) {
        return 'ok, back to marketplace';
      }
      return 'update request';
    }
  },
  async created () {
    await this.getAgencySeatRequests();
  },
  methods: {
    increment () {
      this.currentSessionQuantityChange++;
    },
    decrement () {
      this.currentSessionQuantityChange--;
    },
    async submit () {
      this.displayedSeatCount === this.currentPlusPendingSeats && !this.noPendingRequest ? this.returnToMarketplace() : await this.sendSeatChangeRequest();
    },
    returnToMarketplace () {
      this.$router.push('/marketplace');
    },
    validateSeatChangeRequest () {
      if (this.displayedSeatCount < 3) {
        return false;
      }

      if (this.noPendingRequest && this.calcSeatsDifference === 0) {
        return false;
      }

      return true;
    },
    async sendSeatChangeRequest () {
      this.updatingSeatRequest = true;
      if (!this.validateSeatChangeRequest()) {
        return;
      }

      var payload = {
        AgencyId: this.agency.agencyId,
        AgencyName: this.agency.name,
        RequestedBy: this.agencyUser.name,
        Quantity: this.calcSeatsDifference,
        TargetSeatTotal: this.displayedSeatCount,
        AgencyType: 0
      };

      // existing requests will have an id, otherwise the user is creating a new request
      var isUpdate = this.pendingRequest.hasOwnProperty('id');

      if (isUpdate) {
        payload.Id = this.pendingRequest.id;
      }

      var response = isUpdate ? await this.$api.updateAgencySeatRequest(payload) : await this.$api.createAgencySeatRequest(payload);
      if (response.code !== 0 && response.code !== 2) {
        this.displayErrorAlert = true;
        this.updatingSeatRequest = false;
        return;
      }

      if (this.calcSeatsDifference === 0) {
        this.pendingRequest = {};
        this.newSeatChangeRequestCreated = false;
      } else {
        this.$set(this.pendingRequest, 'quantity', this.calcSeatsDifference);
        if (!isUpdate) {
          var rs = JSON.parse(response.data[0]);
          this.$set(this.pendingRequest, 'id', rs.data[0]);
          this.newSeatChangeRequestCreated = true;
        } else {
          this.newSeatChangeRequestCreated = false;
        }
      }
      this.currentSessionQuantityChange = 0;
      this.requestSent = true;
      this.updatingSeatRequest = false;
    },
    async getAgencySeatRequests () {
      var resp = await this.$api.getAgencySeatRequests(this.agency.agencyId);
      if (resp.code === 0) {
        var payload = JSON.parse(resp.data[0]);
        if (payload.code !== 'NoData') {
          this.pendingRequest = payload.data[0];
        }
      } else if (resp.data.code > 2) {
        this.errorMessage = 'Error retrieving current seat change requests';
      }
      this.loading = false;
    }
  }
};
</script>

<style scoped>
  .text-start {
    text-align: start
  }
  .underline {
    text-decoration: underline
  }
  .hs-blue {
    color: #004C97
  }
</style>
